import { createContext, createElement, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { useBlocker } from "react-router-dom";

const draftChangeSelector = '[role="option"], [role="checkbox"], [role="radio"], [data-draft-change]';

type DraftRegistry = {
  setDirty: (id: string, dirty: boolean) => void;
};

const DraftRegistryContext = createContext<DraftRegistry>({
  setDirty: () => undefined,
});

export function DialogDraftNavigationGuard({ children }: { children: ReactNode }): JSX.Element {
  const [dirtyDrafts, setDirtyDrafts] = useState<ReadonlySet<string>>(() => new Set());
  const blocker = useBlocker(dirtyDrafts.size > 0);
  const { state, proceed, reset } = blocker;
  const setDirty = useCallback((id: string, dirty: boolean): void => {
    setDirtyDrafts((current) => {
      if (current.has(id) === dirty) return current;
      const next = new Set(current);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (state !== "blocked") return;
    if (window.confirm("Discard unsaved changes?")) proceed();
    else reset();
  }, [proceed, reset, state]);
  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (dirtyDrafts.size === 0) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirtyDrafts.size]);

  const registry = useMemo(() => ({ setDirty }), [setDirty]);
  return createElement(DraftRegistryContext.Provider, { value: registry }, children);
}

export function useDialogDraft(): {
  reset: () => void;
  onChangeCapture: (event: FormEvent<HTMLElement>) => void;
  onClickCapture: (event: MouseEvent<HTMLElement>) => void;
  onKeyDownCapture: (event: KeyboardEvent<HTMLElement>) => void;
  canClose: (pending: boolean) => boolean;
} {
  const dirty = useRef(false);
  const id = useId();
  const registry = useContext(DraftRegistryContext);
  const reset = useCallback((): void => {
    dirty.current = false;
    registry.setDirty(id, false);
  }, [id, registry]);
  const onChangeCapture = useCallback((_event: FormEvent<HTMLElement>): void => {
    dirty.current = true;
    registry.setDirty(id, true);
  }, [id, registry]);
  const onClickCapture = useCallback((event: MouseEvent<HTMLElement>): void => {
    if (event.target instanceof Element && event.target.closest(draftChangeSelector)) {
      dirty.current = true;
      registry.setDirty(id, true);
    }
  }, [id, registry]);
  const onKeyDownCapture = useCallback((event: KeyboardEvent<HTMLElement>): void => {
    if ((event.key === "Enter" || event.key === " ") &&
        event.target instanceof Element && event.target.closest(draftChangeSelector)) {
      dirty.current = true;
      registry.setDirty(id, true);
    }
  }, [id, registry]);
  const canClose = useCallback((pending: boolean): boolean => {
    if (pending) return false;
    if (dirty.current && !window.confirm("Discard unsaved changes?")) return false;
    reset();
    return true;
  }, [reset]);
  useEffect(() => () => registry.setDirty(id, false), [id, registry]);

  return {
    reset,
    onChangeCapture,
    onClickCapture,
    onKeyDownCapture,
    canClose,
  };
}
